import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { INSIGHTS } from '../data/insights';

const SITE_URL = 'https://loanyardcapital.com';
const formatDate = (iso) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

const InsightPage = () => {
  const { slug } = useParams();
  const article = INSIGHTS.find((a) => a.slug === slug);
  const next = article ? INSIGHTS.find((a) => a.slug === article.relatedArticle) : null;

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} | LOANYARD CAPITAL`;
    if (document.getElementById('insight-article-schema')) return;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      datePublished: article.date,
      articleSection: article.category,
      description: article.excerpt,
      url: `${SITE_URL}/insights/${article.slug}`,
      author: { '@type': 'Organization', name: 'LOANYARD CAPITAL' },
      publisher: { '@type': 'Organization', name: 'LOANYARD CAPITAL', url: SITE_URL }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'insight-article-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('insight-article-schema');
      if (el) el.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen">
      <Header />
      <WhatsAppButton />

      <div className="bg-white pt-32 pb-16">
        <div className="container mx-auto px-4">
          <article className="max-w-3xl mx-auto" data-testid="insight-article">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-500 mb-6">
              <Link to="/" className="hover:text-emerald-600 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-slate-700 font-medium">Capital Insights</span>
            </nav>

            <div className="flex items-center gap-3 text-sm mb-5">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full font-semibold text-xs">{article.category}</span>
              <span className="text-slate-400">{formatDate(article.date)}</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-8">{article.title}</h1>

            <div className="space-y-5">
              {article.body.map(([tag, text], i) =>
                tag === 'h2' ? (
                  <h2 key={i} className="text-2xl font-bold text-slate-900 pt-4">{text}</h2>
                ) : (
                  <p key={i} className="text-lg text-slate-600 leading-relaxed">{text}</p>
                )
              )}
            </div>

            <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200" data-testid="insight-related-links">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Related Capital Solutions</h3>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {article.related.map(([label, to]) => (
                  <Link key={to} to={to} className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-sm hover:text-emerald-600 transition-colors">
                    {label} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </div>

            {next && (
              <div className="mt-6 text-sm text-slate-600">
                Next read:{' '}
                <Link to={`/insights/${next.slug}`} className="text-emerald-700 font-semibold hover:text-emerald-600 transition-colors">
                  {next.title}
                </Link>
              </div>
            )}

            <div className="mt-10 p-8 bg-emerald-50 rounded-2xl border border-emerald-200 text-center">
              <p className="text-lg text-slate-700 mb-4 font-medium">Evaluating a similar financing requirement?</p>
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
                data-testid="insight-discuss-cta"
              >
                Discuss Your Requirement <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InsightPage;
